/**
 * Cross-component signal for "a ticket's status/assignment changed".
 *
 * There is no shared query cache in this app (see DashboardLayout's nav
 * badge), so pages that mutate a ticket can't rely on a cache invalidation
 * to refresh other surfaces reading ticket state — the nav badge and the
 * notification bell both need to know when to refetch. A tiny in-memory
 * subscriber list is the proportionate fix: notify after a mutation,
 * listeners refetch their own counts.
 */
import { useEffect } from "react";

type Listener = () => void;

const listeners = new Set<Listener>();

export function notifyTicketsChanged(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeToTicketsChanged(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Runs `onChange` whenever any page reports a ticket mutation. */
export function useTicketsChangedListener(onChange: () => void): void {
  useEffect(() => subscribeToTicketsChanged(onChange), [onChange]);
}
